import { attach, createEvent, createStore, sample } from 'effector'
import { produce } from 'immer'

import { type Language, langCodeLocaleMap } from '@/shared/lib/i18n'

import { chatapi, type MessageTranslateRequest, type MessageTranslation } from '../api'
import * as channelModel from './model.channel'

// message_id -> lang -> translation
export const $messageTranslation = createStore<Record<string, Record<string, MessageTranslation>>>({})
export const $messageTranslationShowState = createStore<Record<string, Record<string, boolean>>>({})

// translate both message and reply. Visibility is toggled only for the original message
export const messageTranslate = createEvent<{
    message_id: string
    reply_message_id?: string
    target_lang: Language
}>()

// translate multiple messages and toggle visibility for each one
export const messageTranslateMany = createEvent<{
    message_id_list: string[]
    target_lang: Language
}>()

export const messageTranslateFx = attach({
    effect: chatapi.messageTranslate,
    mapParams: (params: MessageTranslateRequest): MessageTranslateRequest => ({
        ...params,
        // NOTE: we use lang code with region because google translate api requires it for some languages like zh_CN
        target_lang: langCodeLocaleMap[params.target_lang as Language],
    }),
})

// toggle translation visibility
sample({
    clock: [
        // toggle visibility for multiple messages
        messageTranslateMany,
        // toggle visibility only for the original message
        messageTranslate.map(({ message_id, target_lang }) => ({
            message_id_list: [message_id],
            target_lang,
        })),
    ],
    source: $messageTranslationShowState,
    fn: (showIndex, { message_id_list, target_lang }) =>
        produce(showIndex, (state) => {
            for (const message_id of message_id_list) {
                const byLang = (state[message_id] ||= {})
                byLang[target_lang] = !byLang[target_lang]
            }
        }),
    target: $messageTranslationShowState,
})

// prepare translation request by filtering out already translated messages
const translationRequest = sample({
    clock: [
        messageTranslateMany,
        messageTranslate.map(({ message_id, reply_message_id, target_lang }) => ({
            message_id_list: reply_message_id ? [message_id, reply_message_id] : [message_id],
            target_lang,
        })),
    ],
    source: $messageTranslation,
    fn: (translationIndex, { message_id_list, target_lang }) => {
        const request: MessageTranslateRequest = {
            message_id_list: [],
            target_lang,
        }

        // send request only for messages without translation
        for (const message_id of message_id_list) {
            if (!translationIndex[message_id]?.[target_lang]) {
                request.message_id_list.push(message_id)
            }
        }

        return request
    },
})

// send translation request
sample({
    clock: translationRequest,
    // ensure we have something to translate
    filter: (request) => request.message_id_list.length > 0,
    target: messageTranslateFx,
})

// update translation index
sample({
    clock: messageTranslateFx.done,
    source: $messageTranslation,
    fn: (translationIndex, { params: { target_lang }, result: { translation: translationByMessageId } }) =>
        produce(translationIndex, (state) => {
            for (const [message_id, translation] of Object.entries(translationByMessageId)) {
                const byLang = (state[message_id] ||= {})
                byLang[target_lang] = translation
            }
        }),
    target: $messageTranslation,
})

// --- Auto translate ---

export const channelAutoTranslateToggle = createEvent()
export const $channelAutoTranslate = createStore(false).on(channelAutoTranslateToggle, (state) => !state)

// toggle visibility for all translations when auto translate is enabled/disabled
sample({
    clock: $channelAutoTranslate,
    source: {
        showIndex: $messageTranslationShowState,
        messageById: channelModel.$chatChannelMessageById,
    },
    fn: ({ showIndex, messageById }, autoTranslateEnabled) =>
        produce(showIndex, (state) => {
            for (const [messageId, byLang] of Object.entries(state)) {
                if (!messageById[messageId]) {
                    continue
                }

                for (const lang in byLang) {
                    byLang[lang] = autoTranslateEnabled
                }
            }
        }),
    target: $messageTranslationShowState,
})
